import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ElementsButton } from '../Button';
import { Icon } from '../Icon';
import { File as GQLFile } from '../../../generated/graphql';
import { ImageDocumentPane } from './ImageDocumentPane';
import { PdfDocumentPane } from './PdfDocumentPane';
import { UnsupportedDocumentPane } from './UnsupportedDocumentPane';
import { getDocumentViewerKind } from './util';

export interface DocumentViewerPaneProps {
  file: GQLFile;
  downloadURL?: string;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
}

const PaneLoadingIndicator: FC<{ label: string }> = ({ label }) => (
  <div role="status" aria-label={label} className="flex flex-col items-center gap-3 text-darkMode-gray-light">
    <Icon icon="spinner" className="h-8 w-8" />
  </div>
);

export const DocumentViewerPane: FC<DocumentViewerPaneProps> = ({ file, downloadURL, isLoading, isError, retry }) => {
  const { t: tFile } = useTranslation('file');
  const [renderFailed, setRenderFailed] = useState(false);
  // Bumped on retry so ImageDocumentPane/PdfDocumentPane remount from scratch --
  // a render failure isn't tied to downloadURL changing, so nothing else would
  // otherwise re-trigger their load effects.
  const [renderAttempt, setRenderAttempt] = useState(0);

  useEffect(() => setRenderFailed(false), [downloadURL]);

  const retryRender = () => {
    setRenderFailed(false);
    setRenderAttempt((attempt) => attempt + 1);
  };

  if (isLoading) {
    return <PaneLoadingIndicator label={tFile('document-viewer-loading-label')} />;
  }

  if (isError || !downloadURL) {
    return (
      <div className="flex flex-col items-center gap-4 text-darkMode-gray-light">
        <p role="alert">{tFile('error-unable-to-download')}</p>
        <ElementsButton label={tFile('document-viewer-retry')} theme="secondary" onClick={retry} />
      </div>
    );
  }

  const kind = getDocumentViewerKind(file);

  if (kind === 'other') {
    return <UnsupportedDocumentPane file={file} downloadURL={downloadURL} />;
  }

  if (renderFailed) {
    return <UnsupportedDocumentPane file={file} downloadURL={downloadURL} onRetry={retryRender} />;
  }

  if (kind === 'image') {
    return (
      <ImageDocumentPane
        key={renderAttempt}
        downloadURL={downloadURL}
        fileName={file.name}
        onError={() => setRenderFailed(true)}
      />
    );
  }

  return <PdfDocumentPane key={renderAttempt} downloadURL={downloadURL} onError={() => setRenderFailed(true)} />;
};
